import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";
import { toast } from "sonner";

export default function NewsDetailPage() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${newsId}`)
      .then((r) => setItem(r.data))
      .catch(() => navigate("/actualites", { replace: true }))
      .finally(() => setLoading(false));

    api.get("/news").then((r) => setRelated(r.data.filter((n) => n.id !== newsId).slice(0, 3))).catch(() => {});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [newsId, navigate]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = item ? `${item.title} — A.F.B.` : "";

  const share = (kind) => {
    if (kind === "copy") {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Lien copié !");
      return;
    }
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };
    if (urls[kind]) window.open(urls[kind], "_blank", "width=600,height=600");
  };

  if (loading || !item) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={36} className="text-[#0052CC] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="news-detail-page">
      {/* Hero with article image */}
      <section className="relative h-[70vh] min-h-[460px] overflow-hidden">
        <img src={item.image_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-[#0A0F1A]/70 to-[#0A0F1A]/20" />
        <div className="relative h-full max-w-5xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-14 text-white">
          <Button asChild variant="ghost" className="self-start text-white hover:bg-white/10 mb-6">
            <Link to="/actualites" data-testid="news-back-link"><ArrowLeft size={16} className="mr-2" /> Toutes les actualités</Link>
          </Button>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">
            <FourStars size={11} />
            <span>{item.category}</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] mt-4 max-w-4xl">{item.title}</h1>
          <div className="flex items-center gap-5 mt-5 text-sm text-white/80">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(item.published_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}</span>
            <span className="flex items-center gap-1.5"><Tag size={14} /> {item.category}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="font-display text-xl md:text-2xl text-[#0A0F1A] leading-relaxed font-medium" data-testid="news-summary">
            {item.summary}
          </p>
          <StarsDivider className="my-10" />
          <div className="prose-afb space-y-5 text-[#1a2333] text-[16px] md:text-[17px] leading-[1.85]" data-testid="news-content">
            {item.content.split("\n\n").map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* Share */}
          <div className="mt-14 pt-8 border-t border-black/10" data-testid="news-share">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm text-[#475569]">
                <Share2 size={16} className="text-[#0052CC]" /> Partager cette actualité
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => share("facebook")} aria-label="Partager Facebook" data-testid="share-facebook" className="w-10 h-10 rounded-full border border-black/10 hover:bg-[#0052CC] hover:text-white hover:border-[#0052CC] flex items-center justify-center transition-all">
                  <Facebook size={16} />
                </button>
                <button onClick={() => share("twitter")} aria-label="Partager Twitter" data-testid="share-twitter" className="w-10 h-10 rounded-full border border-black/10 hover:bg-[#0A0F1A] hover:text-white hover:border-[#0A0F1A] flex items-center justify-center transition-all">
                  <Twitter size={16} />
                </button>
                <button onClick={() => share("linkedin")} aria-label="Partager LinkedIn" data-testid="share-linkedin" className="w-10 h-10 rounded-full border border-black/10 hover:bg-[#0052CC] hover:text-white hover:border-[#0052CC] flex items-center justify-center transition-all">
                  <Linkedin size={16} />
                </button>
                <button onClick={() => share("copy")} aria-label="Copier le lien" data-testid="share-copy" className="w-10 h-10 rounded-full border border-black/10 hover:bg-[#D4AF37] hover:text-[#0A0F1A] hover:border-[#D4AF37] flex items-center justify-center transition-all">
                  <LinkIcon size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-[#FAFAFA]">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-[#0A0F1A] mb-8">Lire aussi</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((n, i) => (
                <Link key={n.id} to={`/actualites/${n.id}`} data-testid={`related-news-${i}`} className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">{n.category}</div>
                    <h3 className="font-display text-lg text-[#0A0F1A] mt-2 leading-tight line-clamp-2 group-hover:text-[#0052CC]">{n.title}</h3>
                    <div className="text-[11px] text-[#475569] mt-2 flex items-center gap-1.5">
                      <Calendar size={11} /> {new Date(n.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
